import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Upload, Download, MessageSquare, Clock, CheckCircle2, AlertCircle, Eye, DollarSign } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import ChatBox from '../Chat/ChatBox';
import EngineerSidebar from '../Sidebar/EngineerSidebar';
import { parseUTC } from '../../lib/date';

const EngineerDashboard = ({ user }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);

  useEffect(() => {
    loadOrders();
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats/engineer/dashboard`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setDashboardStats(data);
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    }
  };

  const loadOrders = async () => {
    try {
      const { ordersAPI } = await import('../../api');
      const data = await ordersAPI.getOrders();
      setOrders(data);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les commandes",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (orderId, e) => {
    const file = e.target.files[0];
    if (file) {
      // Update order status
      setOrders(orders.map(order => 
        order.id === orderId 
          ? { ...order, status: 'completed', modifiedFile: file.name }
          : order
      ));
      
      toast({
        title: "Fichier uploadé",
        description: `${file.name} a été uploadé avec succès`,
      });
    }
  };

  const handleDownload = (orderId, fileName) => {
    toast({
      title: "Téléchargement",
      description: `Téléchargement de ${fileName}...`,
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500/20 text-green-500 border-green-500/30"><CheckCircle2 className="h-3 w-3 mr-1" />Terminé</Badge>;
      case 'in_progress':
        return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30"><Clock className="h-3 w-3 mr-1" />En cours</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-500 border-gray-500/30"><AlertCircle className="h-3 w-3 mr-1" />En attente</Badge>;
    }
  };

  const pendingOrders = orders.filter(o => o.status === 'in_progress');
  const completedOrders = orders.filter(o => o.status === 'completed');
  const waitingForLogOrders = orders.filter(o => o.status === 'pending');
  
  // Stats from dashboard API
  const ordersInProgress = dashboardStats?.orders?.in_progress || 0;
  const ordersCompleted = dashboardStats?.orders?.completed || 0;
  const consultingsInProgress = dashboardStats?.consultings?.in_progress || 0;
  const consultingsCompleted = dashboardStats?.consultings?.completed || 0;
  const totalRevenue = dashboardStats?.total_revenue || 0;

  return (
    <div className="min-h-screen bg-[#111111]">
      <EngineerSidebar />
      <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">
            Espace Ingénieur - <span className="text-[#C00000]">{user?.name || 'Ingénieur'}</span>
          </h1>
          <p className="text-[#A6A6A6]">Gérez les commandes et livrez les fichiers modifiés</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A6A6A6] text-sm mb-1">Tuning Files en cours</p>
                  <p className="text-3xl font-bold text-white">{ordersInProgress}</p>
                </div>
                <Clock className="h-10 w-10 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A6A6A6] text-sm mb-1">Tuning Files terminés</p>
                  <p className="text-3xl font-bold text-white">{ordersCompleted}</p>
                </div>
                <CheckCircle2 className="h-10 w-10 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20 border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A6A6A6] text-sm mb-1">Consulting en cours</p>
                  <p className="text-3xl font-bold text-white">{consultingsInProgress}</p>
                </div>
                <Clock className="h-10 w-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20 border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A6A6A6] text-sm mb-1">Consulting terminés</p>
                  <p className="text-3xl font-bold text-white">{consultingsCompleted}</p>
                </div>
                <CheckCircle2 className="h-10 w-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20 border-l-4 border-l-[#C00000]">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A6A6A6] text-sm mb-1">CA Total</p>
                  <p className="text-3xl font-bold text-[#C00000]">{totalRevenue.toFixed(0)}€</p>
                </div>
                <DollarSign className="h-10 w-10 text-[#C00000]" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="bg-[#1a1a1a] mb-6">
            <TabsTrigger value="pending" className="data-[state=active]:bg-[#C00000] data-[state=active]:text-white">
              Tuning Files en cours ({pendingOrders.length})
            </TabsTrigger>
            <TabsTrigger value="waiting_for_log" className="data-[state=active]:bg-[#C00000] data-[state=active]:text-white">
              En attente de log ({waitingForLogOrders.length})
            </TabsTrigger>
            <TabsTrigger value="completed" className="data-[state=active]:bg-[#C00000] data-[state=active]:text-white">
              Tuning Files terminés ({completedOrders.length})
            </TabsTrigger>
          </TabsList>

          {/* Pending Orders */}
          <TabsContent value="pending">
            <div className="grid grid-cols-1 gap-6">
              {pendingOrders.map((order) => (
                <Card key={order.id} className="bg-[#1a1a1a] border-[#A6A6A6]/20 border-l-4 border-l-yellow-500">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-white text-lg mb-2">
                          {order.vehicle_info?.brand} {order.vehicle_info?.model}
                        </CardTitle>
                        <div className="flex gap-3 text-sm text-[#A6A6A6]">
                          <span>{order.order_id}</span>
                          <span>•</span>
                          <span>{order.service}</span>
                          <span>•</span>
                          <span>{order.client_name}</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        {getStatusBadge(order.status)}
                        {order.payment_status === 'paid' ? (
                          <Badge className="bg-green-500/20 text-green-500 border-green-500/30 text-xs">
                            💳 Payé
                          </Badge>
                        ) : (
                          <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30 text-xs">
                            ⏳ En attente
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-[#A6A6A6] text-sm mb-1">Date</p>
                          <p className="text-white font-semibold">{order.created_at ? parseUTC(order.created_at).toLocaleDateString('fr-FR') : 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-[#A6A6A6] text-sm mb-1">Prix</p>
                          <p className="text-white font-semibold">{order.price}€ HT</p>
                        </div>
                        <div>
                          <p className="text-[#A6A6A6] text-sm mb-1">Fichier original</p>
                          <p className="text-white font-semibold text-sm truncate">{order.original_file_name || 'N/A'}</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3 pt-4 border-t border-[#A6A6A6]/20">
                        <Button
                          onClick={() => navigate(`/engineer/orders/${order.order_id}`)}
                          className="bg-[#C00000] hover:bg-[#C00000]/90"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Voir les détails
                        </Button>
                        <Button
                          onClick={() => handleDownload(order.id, order.originalFile)}
                          variant="outline"
                          className="border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Télécharger
                        </Button>
                        <div className="flex items-center gap-2">
                          <input
                            id={`upload-${order.id}`}
                            type="file"
                            onChange={(e) => handleFileUpload(order.id, e)}
                            className="hidden"
                            accept=".bin,.hex,.kess,.ori"
                          />
                          <Button
                            onClick={() => document.getElementById(`upload-${order.id}`).click()}
                            variant="outline"
                            className="border-green-500 text-green-500 hover:bg-green-500/10"
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Upload V1
                          </Button>
                        </div>
                        <Button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowChat(true);
                          }}
                          variant="outline"
                          className="border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Chat
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {pendingOrders.length === 0 && (
                <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                  <CardContent className="py-12 text-center">
                    <Clock className="h-16 w-16 text-[#A6A6A6] mx-auto mb-4" />
                    <p className="text-[#A6A6A6] text-lg">Aucune commande en cours</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Waiting for Log Orders */}
          <TabsContent value="waiting_for_log">
            <div className="grid grid-cols-1 gap-6">
              {waitingForLogOrders.map((order) => (
                <Card key={order.id} className="bg-[#1a1a1a] border-[#A6A6A6]/20 border-l-4 border-l-orange-500">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-white text-lg mb-2">
                          {order.vehicle_info.brand} {order.vehicle_info.model}
                        </CardTitle>
                        <div className="flex gap-3 text-sm text-[#A6A6A6]">
                          <span>{order.order_id}</span>
                          <span>•</span>
                          <span>{order.service}</span>
                          <span>•</span>
                          <span>{order.client_name}</span>
                        </div>
                      </div>
                      <Badge className="bg-orange-500/20 text-orange-500 border-orange-500/30">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        En attente de log
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex justify-between items-center">
                      <div className="flex gap-4">
                        <Button
                          onClick={() => navigate(`/engineer/orders/${order.order_id}`)}
                          variant="outline"
                          className="border-[#C00000] text-[#C00000] hover:bg-[#C00000]/10"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Voir détails
                        </Button>
                        <Button
                          onClick={() => setShowChat(order.id)}
                          variant="outline"
                          className="border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Chat
                        </Button>
                      </div>
                      <span className="text-[#C00000] font-bold text-lg">{order.price}€</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {waitingForLogOrders.length === 0 && (
                <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                  <CardContent className="py-12 text-center">
                    <AlertCircle className="h-12 w-12 text-[#A6A6A6] mx-auto mb-4" />
                    <p className="text-[#A6A6A6] text-lg">Aucune commande en attente de log</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Completed Orders */}
          <TabsContent value="completed">
            <div className="grid grid-cols-1 gap-6">
              {completedOrders.map((order) => (
                <Card key={order.id} className="bg-[#1a1a1a] border-[#A6A6A6]/20 border-l-4 border-l-green-500">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-white text-lg mb-2">
                          {order.vehicle_info?.brand} {order.vehicle_info?.model}
                        </CardTitle>
                        <div className="flex gap-3 text-sm text-[#A6A6A6]">
                          <span>{order.order_id}</span>
                          <span>•</span>
                          <span>{order.service}</span>
                          <span>•</span>
                          <span>{order.client_name}</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        {getStatusBadge(order.status)}
                        {order.payment_status === 'paid' ? (
                          <Badge className="bg-green-500/20 text-green-500 border-green-500/30 text-xs">
                            💳 Payé
                          </Badge>
                        ) : (
                          <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30 text-xs">
                            ⏳ En attente
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-[#A6A6A6] text-sm mb-1">Date</p>
                          <p className="text-white font-semibold">{order.created_at ? parseUTC(order.created_at).toLocaleDateString('fr-FR') : 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-[#A6A6A6] text-sm mb-1">Prix</p>
                          <p className="text-white font-semibold">{order.price}€ HT</p>
                        </div>
                        <div>
                          <p className="text-[#A6A6A6] text-sm mb-1">Versions livrées</p>
                          <p className="text-green-500 font-semibold text-sm">
                            {order.modified_files?.length || 0} fichier(s)
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3 pt-4 border-t border-[#A6A6A6]/20">
                        <Button
                          onClick={() => navigate(`/engineer/orders/${order.order_id}`)}
                          data-testid={`view-completed-order-${order.order_id}`}
                          className="bg-[#C00000] hover:bg-[#C00000]/90"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Voir les détails
                        </Button>
                        <Button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowChat(true);
                          }}
                          variant="outline"
                          className="border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Chat
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {completedOrders.length === 0 && (
                <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                  <CardContent className="py-12 text-center">
                    <CheckCircle2 className="h-12 w-12 text-[#A6A6A6] mx-auto mb-4" />
                    <p className="text-[#A6A6A6] text-lg">Aucune commande terminée</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>

      {/* Chat Modal */}
      {showChat && selectedOrder && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
          <div className="w-full max-w-2xl">
            <ChatBox
              orderId={selectedOrder?.order_id}
              userRole="engineer"
              onClose={() => setShowChat(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EngineerDashboard;
